import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { StockPopupComponent } from 'shared/components/organisms';
import { ButtonColor, DialogResponseEnum } from 'shared/enums';
import { PopupService } from 'shared/services';

export interface PermissionStatus {
    camera: boolean;
    geolocation: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ImagePermissionService {
    protected readonly translateService = inject(TranslateService);
    public readonly popupService = inject(PopupService);

    private permissionDialogRef!: MatDialogRef<StockPopupComponent>;
    private destroy$ = new Subject<void>();

    private permissionStatus = new BehaviorSubject<PermissionStatus>({
        camera: false,
        geolocation: false
    });

    public async requestCameraPermission(): Promise<boolean> {
        try {
            if ('permissions' in navigator) {
                const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });

                if (permissionStatus.state === 'granted') {
                    const currentPerms = this.permissionStatus.value;
                    this.permissionStatus.next({ ...currentPerms, camera: true });
                    return true;
                }

                if (permissionStatus.state === 'prompt') {
                    return await this.invokeUserMediaDialog();
                }
                return false;
            }

            return await this.invokeUserMediaDialog();
        } catch (error) {
            console.error('Camera permission denied:', error);
            return false;
        }
    }

    private async invokeUserMediaDialog(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach((track) => track.stop());
            const currentPerms = this.permissionStatus.value;
            this.permissionStatus.next({ ...currentPerms, camera: true });
            return true;
        } catch (error) {
            console.error('Camera permission denied:', error);
            return false;
        }
    }

    public async requestGeolocationPermission(): Promise<boolean> {
        if (navigator.permissions && navigator.permissions.query) {
            try {
                // Query the permission state for geolocation
                const permissionStatus = await navigator.permissions.query({
                    name: 'geolocation' as PermissionName,
                });

                if (permissionStatus.state === 'granted') {
                    // Permission already granted, no need to ask again
                    this.updateGeolocationPermissionStatus(true);
                    return true;
                } else if (permissionStatus.state === 'prompt') {
                    // Permission needs to be requested
                    return await this.getLocationWithPrompt();
                } else {
                    // Permission explicitly denied
                    console.warn('Geolocation permission denied');
                    this.updateGeolocationPermissionStatus(false);
                    return false;
                }
            } catch (error) {
                console.error('Error using Permissions API for geolocation:', error);
            }
        }

        // Fallback for browsers without Permissions API
        return await this.getLocationWithPrompt();

    }

    private getLocationWithPrompt(): Promise<boolean> {
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('User granted geolocation access:', position);
                    this.updateGeolocationPermissionStatus(true);
                    resolve(true);
                },
                (error) => {
                    console.error('Geolocation permission denied:', error);
                    this.updateGeolocationPermissionStatus(false);
                    resolve(false);
                }
            );
        });
    }

    private updateGeolocationPermissionStatus(isGranted: boolean): void {
        const currentPerms = this.permissionStatus.value;
        this.permissionStatus.next({ ...currentPerms, geolocation: isGranted });
    }


    public getPermissionStatus(): Observable<PermissionStatus> {
        return this.permissionStatus.asObservable();
    }

    public hasPermission(type: keyof PermissionStatus): Observable<boolean> {
        return this.permissionStatus.pipe(
            map(status => status[type])
        );
    }

    public showPermissionDeniedDialog(cameraPermissionRequired = true): void {
        this.permissionDialogRef = this.popupService.dialog(
            this.translateService.instant('imageCapture.mediaPermissionTitle'),
            this.translateService.instant(cameraPermissionRequired ? 'imageCapture.cameraPermissionBody': 'imageCapture.locationPermissionBody' ),
            {
                okButton: {
                    text: 'global.ok',
                    action: this.closePermissionDialog.bind(this),
                    color: ButtonColor.PRIMARY,
                }
            },
            { showHeaderBorder: false },
        );

        this.permissionDialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(async (d) => {
                if (d !== DialogResponseEnum.Ok && d !== DialogResponseEnum.Cancel) {
                    await this.closePermissionDialog();
                }
            });
    }
    private async closePermissionDialog(): Promise<void>
    {
        await this.openSettings();

        this.permissionDialogRef.close();
    }

    private async openSettings(): Promise<void> {
        if ('permissions' in navigator) {
            try {
                // Try to trigger camera permission dialog
                await navigator.permissions.query({ name: 'camera' as PermissionName });

                // Try to trigger location permission dialog
                await navigator.permissions.query({ name: 'geolocation' });

                // Alternative approach to trigger permission prompts
                // This might be needed in some browsers
                await Promise.all([
                    navigator.mediaDevices.getUserMedia({ video: true })
                        .catch(() => console.log('Camera permission dialog triggered')),
                    new Promise((resolve) => {
                        navigator.geolocation.getCurrentPosition(
                            () => resolve(true),
                            () => resolve(false)
                        );
                    })
                ]);
            } catch (error) {
                console.warn('Could not open permission settings:', error);
            }
        }
    }
}
